import React, { JSX } from 'react';
import GenericCard, { GenericContentType } from './GenericCard';
import GenericCarousel from './GenericCarousel';
import { ANALYTICS_EVENT_TYPES, logEvent } from '../analytics';
import generalConfig from '../resources/general.json';

import { AmpFlagSet } from '../amplitude';

export interface PharmacyMedication {
    name: string;
    refills: number;
    prescriptionNumber: string;
}

export interface PharmacyMedicationInfo {
    name: string;
    address: string;
    city: string;
    state: string;
    zipcode: string;
    phone: string;
    medications: PharmacyMedication[];
}

export interface MedicationListAttachmentData {
    pharmacies: PharmacyMedicationInfo[];
    type: string;
    removeBubble: boolean;
    hideAvatar: boolean;
    noMargin: boolean;
}

function getPhoneNumbersOnly(unformattedPhone: string): string {
    return unformattedPhone.replace(/[^0-9]/g, '');
}

function toPhoneDisplay(phoneNumbers: string): string {
    return `(${phoneNumbers.substr(0, 3)}) ${phoneNumbers.substr(
        3,
        3
    )}-${phoneNumbers.substr(6, 4)}`;
}

function logPhoneHrefClick() {
    logEvent(ANALYTICS_EVENT_TYPES.MED_CALL_PHARMACY, {
        click_call_type: 'number',
    });
}

export const MedicationList: React.FC<{
    featureFlags: AmpFlagSet;
    pharmacies: PharmacyMedicationInfo[];
}> = ({ featureFlags, pharmacies }) => {
    const pharmacyElements: JSX.Element[] = [];
    for (let i = 0; i < pharmacies.length; i++) {
        const pharmacy = pharmacies[i];
        const phoneNumbers = pharmacy.phone
            ? getPhoneNumbersOnly(pharmacy.phone)
            : '';
        const phoneDisplay =
            pharmacy.phone && phoneNumbers.length === 10
                ? toPhoneDisplay(phoneNumbers)
                : '';

        const medicationElts = pharmacy.medications.map(
            (medication: PharmacyMedication) => {
                return {
                    type: GenericContentType.Quantity,
                    title: { text: medication.name },
                    details: { text: `Rx# ${medication.prescriptionNumber}` },
                    quantity: medication.refills,
                    quantityEmphasizeZero: true,
                    label: generalConfig.providence.medicationListRefills,
                };
            }
        );
        const longPharmacyName = pharmacy.name.split(' - ').length >= 3;
        const pharmacySubtitle = longPharmacyName
            ? pharmacy.address
            : `${pharmacy.address}, ${pharmacy.city}, ${pharmacy.state} ${pharmacy.zipcode}`;
        pharmacyElements.push(
            <GenericCard
                data={{
                    title: { text: pharmacy.name },
                    subtitles: [
                        { text: pharmacySubtitle },
                        {
                            text: phoneDisplay,
                            isPhoneNumber: true,
                            newTab: true,
                            phoneNumberNumerical: phoneNumbers,
                            onClick: logPhoneHrefClick,
                        },
                    ],
                    content: medicationElts,
                    buttonData: {
                        title: generalConfig.providence
                            .medicationListCallPharmacy,
                        // Note: there is an issue in mobile webview where it is not handling
                        // all types of navigation button clicks.  Rendering as an anchor
                        // is the best workaround.
                        renderAsAnchor: true,
                        url: 'tel:' + phoneNumbers,
                        newTab: true,
                        amplitudeEvent: {
                            eventType: ANALYTICS_EVENT_TYPES.MED_CALL_PHARMACY,
                            eventProperties: {
                                click_call_type: 'CTA',
                            },
                        },
                    },
                }}
                featureFlags={featureFlags}
            />
        );
    }
    return <GenericCarousel elements={pharmacyElements} />;
};

export default MedicationList;
